import React, { useState, useEffect, useCallback } from 'react';
import {
  CalendarOff, Plus, Trash2, ChevronLeft, ChevronRight,
  Eye, EyeOff, Loader2, AlertCircle, CheckCircle, Info
} from 'lucide-react';
import deliveryService from '../../services/deliveryService';

const MONTHS = ['January','February','March','April','May','June',
                 'July','August','September','October','November','December'];
const DAYS   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const today     = () => new Date();
const toYMD     = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
const toDisplay = (ymd) => {
  const [y,m,d] = ymd.split('-');
  return `${parseInt(d)} ${MONTHS[parseInt(m)-1]} ${y}`;
};

const ServiceCalendar = () => {
  const [holidays,    setHolidays]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [viewYear,    setViewYear]    = useState(today().getFullYear());
  const [viewMonth,   setViewMonth]   = useState(today().getMonth());
  const [selected,    setSelected]    = useState(null); // YMD string
  const [reason,      setReason]      = useState('');
  const [isPublic,    setIsPublic]    = useState(true);
  const [adding,      setAdding]      = useState(false);
  const [deletingId,  setDeletingId]  = useState(null);
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');
  const [showForm,    setShowForm]    = useState(false);
  // Bulk repeat
  const [bulkMode,    setBulkMode]    = useState(false);
  const [bulkDay,     setBulkDay]     = useState('0'); // 0=Sun … 6=Sat
  const [bulkMonths,  setBulkMonths]  = useState('3');
  const [bulkReason,  setBulkReason]  = useState('');
  const [bulkAdding,  setBulkAdding]  = useState(false);

  const fetchHolidays = useCallback(async () => {
    setLoading(true);
    const result = await deliveryService.getAllHolidays();
    if (result.success) setHolidays(result.data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchHolidays(); }, [fetchHolidays]);

  const holidayMap = Object.fromEntries(holidays.map(h => [h.date, h]));

  const showMsg = (type, msg) => {
    if (type === 'success') { setSuccess(msg); setError(''); }
    else { setError(msg); setSuccess(''); }
    setTimeout(() => { setSuccess(''); setError(''); }, 3500);
  };

  // Calendar grid
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const handleDayClick = (day) => {
    const ymd = `${viewYear}-${String(viewMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    if (holidayMap[ymd]) return; // already marked — use delete from list
    setSelected(ymd);
    setReason('');
    setIsPublic(true);
    setShowForm(true);
    setBulkMode(false);
  };

  const handleAdd = async () => {
    if (!selected) return;
    setAdding(true);
    const result = await deliveryService.createHoliday(selected, reason.trim(), isPublic);
    if (result.success) {
      showMsg('success', `${toDisplay(selected)} marked as no-service day.`);
      setShowForm(false);
      setSelected(null);
      fetchHolidays();
    } else {
      showMsg('error', result.error || 'Failed to add holiday');
    }
    setAdding(false);
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    const result = await deliveryService.deleteHoliday(id);
    if (result.success) {
      showMsg('success', 'No-service day removed.');
      fetchHolidays();
    } else {
      showMsg('error', result.error || 'Failed to remove holiday');
    }
    setDeletingId(null);
  };

  const handleTogglePublic = async (h) => {
    const result = await deliveryService.updateHoliday(h.id, { isPublic: !h.isPublic });
    if (result.success) fetchHolidays();
  };

  const handleBulkAdd = async () => {
    setBulkAdding(true);
    const targetDay = parseInt(bulkDay);
    const months = parseInt(bulkMonths);
    const start = new Date();
    const end = new Date();
    end.setMonth(end.getMonth() + months);

    const dates = [];
    const cur = new Date(start);
    // Advance to first occurrence of targetDay
    while (cur.getDay() !== targetDay) cur.setDate(cur.getDate() + 1);
    while (cur <= end) {
      dates.push(toYMD(cur));
      cur.setDate(cur.getDate() + 7);
    }

    let added = 0;
    let skipped = 0;
    for (const date of dates) {
      if (holidayMap[date]) { skipped++; continue; }
      const r = await deliveryService.createHoliday(date, bulkReason.trim(), true);
      if (r.success) added++;
    }
    showMsg('success', `Added ${added} no-service days${skipped > 0 ? `, skipped ${skipped} already marked` : ''}.`);
    setBulkMode(false);
    fetchHolidays();
    setBulkAdding(false);
  };

  const todayYMD = toYMD(today());
  const upcomingHolidays = holidays.filter(h => h.date >= todayYMD).slice(0, 20);
  const pastHolidays     = holidays.filter(h => h.date <  todayYMD);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Service Calendar</h1>
          <p className="text-gray-600">Mark days when Book my fresh will not deliver</p>
        </div>
        <button
          onClick={() => { setBulkMode(true); setShowForm(false); setSelected(null); }}
          className="flex items-center gap-2 px-4 py-2 bg-[#2e4e18] text-white rounded-lg hover:bg-[#3f6b20] transition-colors text-sm"
        >
          <Plus className="w-4 h-4" /> Bulk Add Recurring Days
        </button>
      </div>

      {/* Alerts */}
      {error   && <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700"><AlertCircle className="w-5 h-5 flex-shrink-0" />{error}</div>}
      {success && <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700"><CheckCircle className="w-5 h-5 flex-shrink-0" />{success}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6">
          {/* Month nav */}
          <div className="flex items-center justify-between mb-4">
            <button onClick={prevMonth} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronLeft className="w-5 h-5" /></button>
            <h2 className="text-lg font-bold text-gray-800">{MONTHS[viewMonth]} {viewYear}</h2>
            <button onClick={nextMonth} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronRight className="w-5 h-5" /></button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-2">
            {DAYS.map(d => (
              <div key={d} className="text-center text-xs font-semibold text-gray-400 py-2">{d}</div>
            ))}
          </div>

          {/* Cells */}
          <div className="grid grid-cols-7 gap-1">
            {cells.map((day, idx) => {
              if (!day) return <div key={`e-${idx}`} />;
              const ymd = `${viewYear}-${String(viewMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
              const isHoliday  = !!holidayMap[ymd];
              const isToday    = ymd === todayYMD;
              const isPast     = ymd < todayYMD;
              const isSelected = ymd === selected;
              return (
                <button
                  key={ymd}
                  onClick={() => !isPast && !isHoliday && handleDayClick(day)}
                  title={isHoliday ? (holidayMap[ymd].reason || 'No service') : isPast ? 'Past date' : 'Click to mark as no-service'}
                  className={`
                    aspect-square rounded-lg text-sm font-medium transition-all flex flex-col items-center justify-center gap-0.5
                    ${isHoliday  ? 'bg-red-100 text-red-700 cursor-default ring-1 ring-red-300'
                    : isSelected ? 'bg-[#548B2E] text-white'
                    : isToday    ? 'ring-2 ring-[#548B2E] text-[#2e4e18] font-bold hover:bg-[#f0f7e8]'
                    : isPast     ? 'text-gray-300 cursor-not-allowed'
                    : 'hover:bg-[#f0f7e8] text-gray-700 cursor-pointer'}
                  `}
                >
                  <span>{day}</span>
                  {isHoliday && <span className="text-[8px] leading-none">No Service</span>}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-4 mt-4 text-xs text-gray-500">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-100 ring-1 ring-red-300 inline-block" /> No-service day</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded ring-2 ring-[#548B2E] inline-block" /> Today</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#f0f7e8] inline-block" /> Click to mark</span>
          </div>
        </div>

        {/* Side panel */}
        <div className="space-y-4">
          {/* Add form */}
          {showForm && selected && (
            <div className="bg-white rounded-xl shadow-sm p-5 border-2 border-[#548B2E]">
              <h3 className="font-bold text-gray-800 mb-1">Mark as No-Service Day</h3>
              <p className="text-sm text-[#548B2E] font-medium mb-4">{toDisplay(selected)}</p>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Reason <span className="text-gray-400">(optional)</span></label>
                  <input
                    type="text"
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder="e.g. Holi, Maintenance..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
                  />
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={isPublic} onChange={e => setIsPublic(e.target.checked)} className="w-4 h-4 accent-[#548B2E]" />
                  <span className="text-sm text-gray-600">Show reason to customers</span>
                </label>
                <div className="flex gap-2 pt-1">
                  <button onClick={() => { setShowForm(false); setSelected(null); }}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                  <button onClick={handleAdd} disabled={adding}
                    className="flex-1 px-3 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600 disabled:opacity-50 flex items-center justify-center gap-1">
                    {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarOff className="w-4 h-4" />}
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Bulk form */}
          {bulkMode && (
            <div className="bg-white rounded-xl shadow-sm p-5 border-2 border-[#2e4e18]">
              <h3 className="font-bold text-gray-800 mb-4">Bulk Recurring Days</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Day of week</label>
                  <select value={bulkDay} onChange={e => setBulkDay(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]">
                    {DAYS.map((d,i) => <option key={i} value={i}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">For the next</label>
                  <select value={bulkMonths} onChange={e => setBulkMonths(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]">
                    <option value="1">1 month</option>
                    <option value="2">2 months</option>
                    <option value="3">3 months</option>
                    <option value="6">6 months</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Reason <span className="text-gray-400">(optional)</span></label>
                  <input type="text" value={bulkReason} onChange={e => setBulkReason(e.target.value)}
                    placeholder="e.g. Weekly off"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]" />
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={() => setBulkMode(false)}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                  <button onClick={handleBulkAdd} disabled={bulkAdding}
                    className="flex-1 px-3 py-2 bg-[#2e4e18] text-white rounded-lg text-sm hover:bg-[#3f6b20] disabled:opacity-50 flex items-center justify-center gap-1">
                    {bulkAdding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    Add All
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Upcoming holidays list */}
          <div className="bg-white rounded-xl shadow-sm p-5">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <CalendarOff className="w-4 h-4 text-red-500" />
              Upcoming No-Service Days
              {upcomingHolidays.length > 0 && (
                <span className="ml-auto text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">{upcomingHolidays.length}</span>
              )}
            </h3>
            {loading ? (
              <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
            ) : upcomingHolidays.length === 0 ? (
              <div className="text-center py-6">
                <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No upcoming no-service days</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {upcomingHolidays.map(h => (
                  <div key={h.id} className={`flex items-start gap-3 p-3 rounded-lg border ${h.date === todayYMD ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-100'}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-gray-800">{toDisplay(h.date)}</p>
                      {h.reason && <p className="text-xs text-gray-500 truncate">{h.reason}</p>}
                      {!h.reason && <p className="text-xs text-gray-400 italic">No reason set</p>}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleTogglePublic(h)}
                        title={h.isPublic ? 'Reason visible to customers' : 'Reason hidden from customers'}
                        className="p-1 hover:bg-gray-200 rounded"
                      >
                        {h.isPublic ? <Eye className="w-3.5 h-3.5 text-green-600" /> : <EyeOff className="w-3.5 h-3.5 text-gray-400" />}
                      </button>
                      <button
                        onClick={() => handleDelete(h.id)}
                        disabled={deletingId === h.id}
                        className="p-1 hover:bg-red-100 rounded text-red-500"
                      >
                        {deletingId === h.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Past holidays count */}
          {pastHolidays.length > 0 && (
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl text-sm text-gray-500">
              <Info className="w-4 h-4 flex-shrink-0" />
              {pastHolidays.length} past no-service day{pastHolidays.length > 1 ? 's' : ''} (not shown)
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceCalendar;